import MuiAlert from "@mui/material/Alert";
import styles from "./AddProduct.module.css";
import Snackbar from "@mui/material/Snackbar";
import { Timestamp } from "firebase/firestore";
import { Product } from "../../models/product.ts";
import { Category } from "../../models/category.ts";
import { SizeOption } from "../../models/size-option.ts";
import { ProductType } from "../../models/product-type.ts";
import { StockOption } from "../../models/stock-option.ts";
import { CategoryType } from "../../models/category-type.ts";
import { ProductDesign } from "../../models/product-design.ts";
import { SalePercentages } from "../../models/sale-percentages.ts";
import { getCategories } from "../../Services/category.service.ts";
import { imagesService } from "../../Services/images.service.ts";
import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { createProduct, editProduct } from "../../Services/product.service.ts";

interface AddProductProps {
  productToEdit?: Product;
  onBack: () => void;
}

export const AddProduct = ({ productToEdit, onBack }: AddProductProps) => {
  const [formData, setFormData] = useState<
    Omit<Product, "images" | "stockOptions" | "stock" | "category"> & {
      frontImage?: File;
      backImage?: File;
      otherImages?: File[];
      frontImagePath?: string;
      backImagePath?: string;
      otherImagePaths?: string[];
      category?: Category;
      stockOptions?: StockOption[];
    }
  >({
    name: "",
    description: "",
    price: 0,
    type: "",
    season: "",
    category: undefined,
    frontImage: undefined,
    backImage: undefined,
    otherImages: [],
    frontImagePath: "",
    backImagePath: "",
    otherImagePaths: [],
    sale: SalePercentages.NONE,
    fabric: undefined,
    design: undefined,
    stockOptions: [],
    isOutOfStock: false,
  });

  const [selectedCategoryType, setSelectedCategoryType] =
    useState<CategoryType>();
  const [selectedProductDesign, setSelectedProductDesign] =
    useState<ProductDesign>();
  const [selectedProductSale, setSelectedProductSale] =
    useState<SalePercentages>();
  const [selectedProductSize, setSelectedProductSize] = useState<SizeOption>();
  const [selectedProductType, setSelectedProductType] = useState<ProductType>();
  const [selectedProductFabric, setSelectedProductFabric] = useState<
    string | undefined
  >();

  // Image preview URLs
  const [frontImageUrl, setFrontImageUrl] = useState<string | undefined>(
    undefined
  );
  const [backImageUrl, setBackImageUrl] = useState<string | undefined>(
    undefined
  );
  const [otherImageUrls, setOtherImageUrls] = useState<string[]>([]);
  const [sizeChartFile, setSizeChartFile] = useState<File | undefined>();
  const [sizeChartUrl, setSizeChartUrl] = useState<string>("");

  const [submitting, setSubmitting] = useState(false);
  const [newQuantity, setNewQuantity] = useState<number>(0);
  const [categories, setCategories] = useState<Category[]>([]);
  const [stockOptions, setStockOptions] = useState<StockOption[]>([]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const [newColor, setNewColor] = useState<{ name: string; hex: string }>({
    name: "",
    hex: "#000000",
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const fetched = await getCategories();
        setCategories(fetched);
      } catch (error) {
        console.error("Fetching categories failed:", error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (productToEdit) {
      setFormData({
        ...productToEdit,
        frontImage: undefined,
        backImage: undefined,
        otherImages: [],
        isOutOfStock: productToEdit.isOutOfStock ?? false,
      });
      // This line was needed to show images of product when edited
      setFrontImageUrl(productToEdit?.images?.front);
      setBackImageUrl(productToEdit?.images?.back);
      setOtherImageUrls(productToEdit?.images?.detail || []);
      setSizeChartUrl(productToEdit.sizeChart || "");
      setSizeChartFile(undefined);

      setSelectedProductType(
        Object.values(ProductType).includes(productToEdit.type as ProductType)
          ? (productToEdit.type as ProductType)
          : undefined
      );

      if (productToEdit.stockOptions) {
        setStockOptions(productToEdit.stockOptions);
      }
    }
  }, [productToEdit]);

  useEffect(() => {
    return () => {
      frontImageUrl && URL.revokeObjectURL(frontImageUrl);
      backImageUrl && URL.revokeObjectURL(backImageUrl);
      otherImageUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [frontImageUrl, backImageUrl, otherImageUrls]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type, files } = e.target as HTMLInputElement;
    if (type === "file") {
      if (name === "frontImage" && files && files.length > 0) {
        setFormData((prev) => ({
          ...prev,
          frontImage: files[0],
          frontImagePath: files[0].name,
        }));
        setFrontImageUrl(URL.createObjectURL(files[0]));
      } else if (name === "backImage" && files && files.length > 0) {
        setFormData((prev) => ({
          ...prev,
          backImage: files[0],
          backImagePath: files[0].name,
        }));
        setBackImageUrl(URL.createObjectURL(files[0]));
      } else if (name === "sizeChart" && files && files.length > 0) {
        setSizeChartFile(files[0]);
        setSizeChartUrl(URL.createObjectURL(files[0]));
      } else if (name === "otherImages" && files && files.length > 0) {
        const fileArr = Array.from(files);
        setFormData((prev) => ({
          ...prev,
          otherImages: [...(prev.otherImages || []), ...fileArr],
          otherImagePaths: [
            ...(prev.otherImagePaths || []),
            ...fileArr.map((file) => file.name),
          ],
        }));
        setOtherImageUrls((prev) => [
          ...prev,
          ...fileArr.map((file) => URL.createObjectURL(file)),
        ]);
      }
    } else if (name === "categoryType") {
      const selectedCategory = categories.find(
        (cat) => cat.name === (value as CategoryType)
      );
      if (selectedCategory) {
        setSelectedCategoryType(value as CategoryType);
        setFormData((prev) => ({
          ...prev,
          category: selectedCategory,
        }));
      }
    } else if (name === "price") {
      setFormData((prev) => ({ ...prev, [name]: parseFloat(value) }));
    } else if (name === "productType") {
      setSelectedProductType(value as ProductType);
      setFormData((prev) => ({ ...prev, type: value }));
    } else if (name === "productSize") {
      setSelectedProductSize(value as SizeOption);
    } else if (name === "productDesign") {
      setSelectedProductDesign(value as ProductDesign);
    } else if (name === "productFabric") {
      setSelectedProductFabric(value);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const removeImage = (
    imageType: "front" | "back" | "other",
    index?: number
  ) => {
    if (imageType === "front") {
      if (frontImageUrl) URL.revokeObjectURL(frontImageUrl);
      setFrontImageUrl(undefined);
      setFormData((prev) => ({
        ...prev,
        frontImage: undefined,
        frontImagePath: "",
      }));
    } else if (imageType === "back") {
      if (backImageUrl) URL.revokeObjectURL(backImageUrl);
      setBackImageUrl(undefined);
      setFormData((prev) => ({
        ...prev,
        backImage: undefined,
        backImagePath: "",
      }));
    } else if (imageType === "other" && index !== undefined) {
      const newUrls = [...otherImageUrls];
      const newFiles = [...(formData.otherImages || [])];
      const newPaths = [...(formData.otherImagePaths || [])];

      URL.revokeObjectURL(newUrls[index]);
      newUrls.splice(index, 1);
      newFiles.splice(index, 1);
      newPaths.splice(index, 1);

      setOtherImageUrls(newUrls);
      setFormData((prev) => ({
        ...prev,
        otherImages: newFiles,
        otherImagePaths: newPaths,
      }));
    }
  };

  const addStockOption = () => {
    const isUnstitched =
      formData.category &&
      formData.category.name?.toLowerCase() === "unstitched";

    if (
      (!isUnstitched && !selectedProductSize) ||
      !newColor.hex ||
      newQuantity <= 0
    ) {
      alert(
        isUnstitched
          ? "Please fill color and quantity for unstitched."
          : "Please fill all stock fields properly."
      );
      return;
    }
    let newStockOption: StockOption = {
      size: isUnstitched ? null : selectedProductSize,
      color: newColor,
      quantity: newQuantity,
    };

    setStockOptions((prev) => [...prev, newStockOption]);
    // Reset form
    setSelectedProductSize(undefined);
    setSelectedProductType(undefined);
    setSelectedProductDesign(undefined);
    setSelectedProductFabric(undefined);
    setSelectedProductSale(undefined);
    setNewQuantity(0);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let sizeChart = sizeChartUrl.startsWith("blob:") ? "" : sizeChartUrl;
      if (sizeChartFile) {
        const uploaded = await imagesService.uploadImage(sizeChartFile);
        if (!uploaded?.publicUrl) {
          throw new Error("Size chart upload failed");
        }
        sizeChart = uploaded.publicUrl;
      }

      const totalStock = stockOptions.reduce((sum, s) => sum + s.quantity, 0);

      const {
        frontImage,
        backImage,
        otherImages,
        frontImagePath,
        backImagePath,
        otherImagePaths,
        ...restFormData
      } = formData;

      const finalData: Product = {
        ...restFormData,
        stock: totalStock,
        stockOptions,
        createdAt:
          productToEdit?.createdAt instanceof Timestamp
            ? productToEdit.createdAt.toDate()
            : productToEdit?.createdAt || new Date(),
        updatedAt: new Date(),
        images: {
          front: frontImageUrl,
          back: backImageUrl,
          detail: otherImageUrls,
        },
        category: formData.category as Category,
        design: formData.design as ProductDesign,
        fabric: formData.fabric,
        sale: formData.sale as SalePercentages,
        isOutOfStock: formData.isOutOfStock || false,
        sizeChart,
      };

      if (productToEdit?.id) {
        await editProduct(productToEdit.id, finalData);
        setSnackbar({
          open: true,
          message: "Product Updated!",
          severity: "success",
        });
        return setTimeout(() => onBack(), 1200);
      } else {
        const result = await createProduct(finalData, {
          front: formData.frontImage,
          back: formData.backImage,
          detail: formData.otherImages,
        });
        if (result.success) {
          setSnackbar({
            open: true,
            message: "Product added!",
            severity: "success",
          });
          setTimeout(() => onBack(), 1200);
        } else throw result.error;
      }
    } catch (error) {
      console.error("Submit error:", error);
      setSnackbar({
        open: true,
        message: "Failed to save product.",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.mainWrapper}>
      <div className={styles.header}>
        <h2 className={styles.title}>Add Product</h2>
      </div>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.leftColumn}>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Description</h3>

              <div className={styles.field}>
                <label className={styles.label}>Product Name</label>
                <input
                  name="name"
                  placeholder="Product name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className={styles.input}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Business Description</label>
                <textarea
                  name="description"
                  placeholder="Product Description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  className={styles.textarea}
                  rows={4}
                />
              </div>
            </div>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Category</h3>
              <div className={styles.field}>
                <label className={styles.label}>Product Category</label>
                <select
                  id="categoryType"
                  name="categoryType"
                  value={formData.category?.name || ""}
                  className={styles.select}
                  onChange={handleInputChange}
                >
                  <option value="">Select Category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Product Fabric</h3>
              <div className={styles.field}>
                <label className={styles.label}>Product Fabric</label>
                <input
                  type="text"
                  id="fabric"
                  name="fabric"
                  value={formData.fabric || ""}
                  placeholder="Enter fabric type"
                  className={styles.input}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Product Design</h3>
              <div className={styles.field}>
                <label className={styles.label}>Select Design</label>
                <select
                  id="design"
                  name="design"
                  value={formData.design || ""}
                  className={styles.select}
                  onChange={handleInputChange}
                >
                  <option value="">Select Design</option>
                  {Object.values(ProductDesign).map((design) => (
                    <option key={String(design)} value={String(design)}>
                      {String(design)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Product Price</h3>
              <div className={styles.field}>
                <label className={styles.label}>Product Price</label>
                <input
                  type="number"
                  name="price"
                  placeholder="Product Price"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  className={styles.input}
                />
              </div>
            </div>
          </div>
          <div className={styles.rightColumn}>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Product Image</h3>
              <div className={styles.imageUploadGrid}>
                {/* Main Image */}
                <div className={styles.mainImageContainer}>
                  {frontImageUrl ? (
                    <div className={styles.imagePreviewContainer}>
                      <img
                        src={frontImageUrl}
                        alt="Front Preview"
                        className={styles.mainImage}
                      />
                      <button
                        type="button"
                        className={styles.removeImageBtn}
                        onClick={() => removeImage("front")}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <label className={styles.imageUploadPlaceholder}>
                      <input
                        type="file"
                        name="frontImage"
                        accept="image/*"
                        onChange={handleInputChange}
                        className={styles.hiddenInput}
                      />
                      <div className={styles.uploadIcon}>📷</div>
                      <p>Drop your images or</p>
                      <span className={styles.browseText}>
                        select click to browse
                      </span>
                    </label>
                  )}
                </div>

                {/* Thumbnail Images */}
                <div className={styles.thumbnailGrid}>
                  <div className={styles.thumbnailContainer}>
                    {backImageUrl ? (
                      <div className={styles.imagePreviewContainer}>
                        <img
                          src={backImageUrl}
                          alt="Back Preview"
                          className={styles.thumbnail}
                        />
                        <button
                          type="button"
                          className={styles.removeImageBtn}
                          onClick={() => removeImage("back")}
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <label className={styles.thumbnailPlaceholder}>
                        <input
                          type="file"
                          name="backImage"
                          accept="image/*"
                          onChange={handleInputChange}
                          className={styles.hiddenInput}
                        />
                        <div className={styles.uploadIcon}>📷</div>
                      </label>
                    )}
                  </div>

                  {otherImageUrls.map((url, index) => (
                    <div key={index} className={styles.thumbnailContainer}>
                      <div className={styles.imagePreviewContainer}>
                        <img
                          src={url}
                          alt={`Detail ${index + 1}`}
                          className={styles.thumbnail}
                        />
                        <button
                          type="button"
                          className={styles.removeImageBtn}
                          onClick={() => removeImage("other", index)}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}

                  <div className={styles.thumbnailContainer}>
                    <label className={styles.thumbnailPlaceholder}>
                      <input
                        type="file"
                        name="otherImages"
                        accept="image/*"
                        onChange={handleInputChange}
                        className={styles.hiddenInput}
                      />
                      <div className={styles.uploadIcon}>📷</div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Size chart</h3>
              <div className={styles.field}>
                <label className={styles.label}>Size guide image</label>
                {sizeChartUrl ? (
                  <div className={styles.sizeChartPreview}>
                    <img src={sizeChartUrl} alt="Size chart preview" />
                    <button
                      type="button"
                      className={styles.removeImageBtn}
                      onClick={() => {
                        if (sizeChartUrl.startsWith("blob:")) {
                          URL.revokeObjectURL(sizeChartUrl);
                        }
                        setSizeChartFile(undefined);
                        setSizeChartUrl("");
                      }}
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <label className={styles.sizeChartDrop}>
                    <input
                      type="file"
                      name="sizeChart"
                      accept="image/*"
                      onChange={handleInputChange}
                      className={styles.hiddenInput}
                    />
                    <div className={styles.uploadIcon}>📷</div>
                    <p>Upload the size chart</p>
                    <span className={styles.browseText}>select click to browse</span>
                  </label>
                )}
              </div>
            </div>

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Product Type</h3>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="productType">
                  Select ProductType:
                </label>
                <select
                  id="productType"
                  name="productType"
                  value={selectedProductType}
                  className={styles.select}
                  onChange={handleInputChange}
                >
                  <option value="">Select Product Type</option>
                  {Object.values(ProductType).map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Sale Percentage</h3>
              <div className={styles.field}>
                <label className={styles.label}>Select Sale Percentage</label>
                <select
                  id="sale"
                  name="sale"
                  value={formData.sale ?? SalePercentages.NONE}
                  className={styles.select}
                  onChange={handleInputChange}
                >
                  {Object.entries(SalePercentages)
                    .filter(([key, value]) => typeof value === "number")
                    .map(([key, value]) => (
                      <option key={key} value={value}>
                        {value === 0 ? "No Sale" : `${value}%`}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Stock Status</h3>
              <div className={styles.field}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    name="isOutOfStock"
                    checked={formData.isOutOfStock || false}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        isOutOfStock: e.target.checked,
                      }))
                    }
                    style={{
                      width: "18px",
                      height: "18px",
                      cursor: "pointer",
                    }}
                  />
                  <span className={styles.label}>Mark as Sold Out</span>
                </label>
              </div>
            </div>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Select Size</h3>

              <div className={styles.sizeSection}>
                <div className={styles.sizeInputRow}>
                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="productSize">
                      Select Product Size:
                    </label>
                    <select
                      id="productSize"
                      name="productSize"
                      value={selectedProductSize}
                      className={styles.select}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Size</option>
                      {Object.values(SizeOption).map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {/* Color Name and Picker */}
                <div
                  className={styles.sizeInputRow}
                  style={{
                    display: "flex",
                    gap: "16px",
                    alignItems: "center",
                    marginTop: "8px",
                  }}
                >
                  <div className={styles.field} style={{ flex: 1 }}>
                    <label className={styles.label} htmlFor="colorName">
                      Color Name
                    </label>
                    <input
                      type="text"
                      id="colorName"
                      name="colorName"
                      placeholder="Enter color name"
                      value={newColor.name}
                      onChange={(e) =>
                        setNewColor((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      className={styles.input}
                    />
                  </div>
                  <div className={styles.field} style={{ flex: 1 }}>
                    <label className={styles.label} htmlFor="colorHex">
                      Color Picker
                    </label>
                    <input
                      type="color"
                      id="colorHex"
                      name="colorHex"
                      value={newColor.hex}
                      onChange={(e) =>
                        setNewColor((prev) => ({
                          ...prev,
                          hex: e.target.value,
                        }))
                      }
                      className={styles.input}
                      style={{ height: "40px", minHeight: "40px", padding: 0 }}
                    />
                  </div>
                </div>
                <div className={styles.quantitySection}>
                  <div className={styles.field}>
                    <label className={styles.label}>Quantity</label>
                    <input
                      type="number"
                      placeholder="Enter quantity"
                      value={newQuantity}
                      onChange={(e) => setNewQuantity(parseInt(e.target.value))}
                      className={styles.input}
                    />
                  </div>
                  <div className={styles.field}>
                    <button
                      type="button"
                      onClick={addStockOption}
                      className={styles.addStockBtn}
                    >
                      Add Stock
                    </button>
                  </div>
                </div>

                {stockOptions.length > 0 && (
                  <div className={styles.stockList}>
                    {stockOptions.map((opt, index) => (
                      <div
                        key={index}
                        className={styles.stockItem}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: "8px",
                          fontSize: "0.95em",
                          background: "#f8f8f8",
                          borderRadius: "6px",
                          marginBottom: "6px",
                          padding: "6px 10px",
                        }}
                      >
                        <span style={{ minWidth: 60 }}>
                          <b>Size:</b> {opt.size || "No Size"}
                        </span>
                        <span style={{ minWidth: 60 }}>
                          <b>Color:</b>{" "}
                          <span
                            style={{
                              background: opt.color.hex,
                              borderRadius: "3px",
                              padding: "0 8px",
                              color: "#222",
                              border: "1px solid #ddd",
                            }}
                          >
                            {opt.color.hex}
                          </span>
                        </span>
                        <span style={{ minWidth: 60 }}>
                          <b>Qty:</b> {opt.quantity}
                        </span>
                        <button
                          type="button"
                          className={styles.removeButton}
                          style={{
                            marginLeft: "auto",
                            background: "#e57373",
                            color: "#fff",
                            borderRadius: "3px",
                            border: "none",
                            padding: "2px 8px",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            const updated = [...stockOptions];
                            updated.splice(index, 1);
                            setStockOptions(updated);
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>{" "}
        </div>

        <div>
          <div className={styles.actionButtons}>
            <button
              type="button"
              onClick={onBack}
              className={styles.discardBtn}
            >
              Discard
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className={styles.saveProductBtn}
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Save Product"}
            </button>
          </div>
        </div>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={2000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <MuiAlert
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
            elevation={6}
            variant="filled"
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </div>
    </div>
  );
};
